import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/clerk';
import { setAuthTokenGetter } from '../../api/client';
import { TEST_MODE } from '../../testMode';
import { keepAppShell } from '../../utils/userLoad';

const TOKEN_READY_POLL_MS = 100;
const TOKEN_READY_WAIT_MS = 2000;

/** A token lookup that never settles must not keep the page on the spinner. */
export function tokenBeforeDeadline(getToken, remainingMs) {
  const cap = Math.max(0, remainingMs);
  return Promise.race([
    Promise.resolve().then(() => getToken()).catch(() => null),
    new Promise((resolve) => {
      setTimeout(() => resolve(null), cap);
    }),
  ]);
}

/**
 * Provider component that connects the API client with Clerk authentication.
 *
 * Must be rendered inside ClerkProvider to access useAuth.
 * Sets up the token getter so all API requests include auth headers.
 * Waits until auth is loaded before rendering children to prevent
 * API calls without auth tokens.
 */
function AuthenticatedApiProvider({ children }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const shellShown = useRef(false);

  useEffect(() => {
    if (!isLoaded) return undefined;
    setAuthTokenGetter(getToken);
    // Test auth never returns a Clerk token. A signed-out page can render
    // now. A signed-in page waits until the token exists, so the first
    // project read is not sent empty.
    if (TEST_MODE || !isSignedIn) {
      setIsReady(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const deadline = Date.now() + TOKEN_READY_WAIT_MS;
      while (!cancelled && Date.now() < deadline) {
        const token = await tokenBeforeDeadline(getToken, deadline - Date.now());
        if (cancelled) return;
        if (token) break;
        if (Date.now() >= deadline) break;
        await new Promise((resolve) => setTimeout(resolve, TOKEN_READY_POLL_MS));
      }
      if (!cancelled) setIsReady(true);
    })();
    return () => { cancelled = true; };
  }, [getToken, isLoaded, isSignedIn]);

  if (isReady) shellShown.current = true;

  // Don't render children until auth is loaded and token getter is set.
  // After the first load, a later unreadiness must not unmount the app.
  if (!keepAppShell({ ready: isReady, shellShown: shellShown.current })) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return children;
}

export default AuthenticatedApiProvider;
